import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Header,
  Segment,
  Checkbox,
  Dropdown,
  Input,
  Button,
  Table,
  Icon
} from 'semantic-ui-react';
import { distanceBetween } from '../../utils';
import { servicesAPI } from '../../_services';
import { Map } from './Map';

export const DisplayMap = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const gotoPostcode = params.get('postcode')
    ? decodeURIComponent(params.get('postcode'))
    : '';

  const [hubLocationInfo, setHubLocationInfo] = useState(
    window.hubLocationInfo
  );
  const [loadingHubInfo, setLoadingHubInfo] = useState(true);

  const [areas, setAreas] = useState([]);
  const [hydraLocation, setHydraLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [mapZoom, setMapZoom] = useState(18);

  const [showHUBs, setShowHUBs] = useState(true);
  // const [showAONs, setShowAONs] = useState(false);
  const [showUnderConstruction, setShowUnderConstruction] = useState(false);
  // const [showCircle, setShowCircle] = useState(true);
  const [showCustomerView, setShowCustomerView] = useState(false);
  const [showControlledParkingZones, setShowControlledParkingZones] = useState(
    false
  );

  const [phalanx, setPhalanx] = useState(null);
  const [findAddress, setFindAddress] = useState('');
  const [postcodeLongLat, setPostcodeLongLat] = useState([0, 0]);
  const [nearestHUBs, setNearestHUBs] = useState(null);
  const [exportParkingData, setExportParkingData] = useState(null);
  const [nightMode, setNightMode] = useState(false);

  useEffect(() => {
    if (!hubLocationInfo && loadingHubInfo) {
      servicesAPI
        .InventoryServices('getPhalanxLocationInfo')
        .then(hubLocationInfo => {
          let newHubLocationInfo = [];
          for (const hydraRef of Object.keys(hubLocationInfo)) {
            newHubLocationInfo.push({
              ...hubLocationInfo[hydraRef],
              reference: hydraRef
            });
          }
          window.hubLocationInfo = newHubLocationInfo;

          setLoadingHubInfo(false);
          setHubLocationInfo(newHubLocationInfo);
        });
    }
  }, [hubLocationInfo, loadingHubInfo]);

  useEffect(() => {
    if (!hubLocationInfo) return;

    let areas = [];
    for (let i = 0; i < hubLocationInfo.length; i++) {
      areas.push(hubLocationInfo[i].local_authority);
    }
    setAreas([...new Set(areas)]);

    // Go to postcode?
    if (gotoPostcode.length >= 2) {
      setFindAddress(gotoPostcode);
      goToAddress(gotoPostcode);
    } else {
      setHydraLocation(hubLocationInfo[0]);
    }
  }, [hubLocationInfo]); // eslint-disable-line

  useEffect(() => {
    if (!hubLocationInfo || !phalanx) return;

    // Identify street from phalanx (if HUB)
    hubLocationInfo.forEach(hydraLocation => {
      if (hydraLocation.description === phalanx.description) {
        setHydraLocation(hydraLocation);
        setFindAddress('');
        goToAddress('');
      }
    });
  }, [hubLocationInfo, phalanx]); // eslint-disable-line

  useEffect(() => {
    if (!hydraLocation) return;

    const latLon = getHydraMiddlePos(hydraLocation);
    setMapCenter([latLon[1], latLon[0]]);
    setMapZoom(mapZoom + 0.00000001); // Tiny adjustment to trigger update
  }, [hydraLocation]); // eslint-disable-line

  useEffect(() => {
    if (!nearestHUBs) return;

    const lon = postcodeLongLat[0];
    const lat = postcodeLongLat[1];

    if (!lon || !lat) return;

    // Find nearest HUB
    let newNearestHUBs = nearestHUBs.map(h => ({
      localAuthority: h.localAuthority,
      hub: null,
      distance: 99999999
    }));

    hubLocationInfo.forEach(hydraLocation => {
      const hydraLongLat = getHydraMiddlePos(hydraLocation);
      const distance = distanceBetween(
        hydraLongLat[0],
        hydraLongLat[1],
        postcodeLongLat[1],
        postcodeLongLat[0]
      );

      const localAuthority = newNearestHUBs.find(
        h => hydraLocation.local_authority === h.localAuthority
      );
      if (localAuthority && distance < localAuthority.distance) {
        localAuthority.hub = hydraLocation;
        localAuthority.distance = distance;
      }
    });

    // Sort nearest to furthest
    const sortedNearestHUBs = newNearestHUBs.sort(
      (h1, h2) => h1.distance - h2.distance
    );
    setNearestHUBs([...sortedNearestHUBs]);
  }, [postcodeLongLat, hubLocationInfo]);

  const getHydraMiddlePos = hydra => {
    const middleChargePointIdx = Math.floor(hydra.lat_lons.length / 2);
    return hydra.lat_lons[middleChargePointIdx];
  };

  const setCenterByPostcode = code => {
    const address = code + ', UK';
    const encodedAddress = encodeURI(address);
    const apiKey = 'AIzaSyA3bCC3k5MfdlaCaS4gtizub3nCWyB3Wjo';
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`
    )
      .then(response => response.json())
      .then(response => {
        if (response.results.length < 1) return;

        // Center map on postcode
        const location = response.results[0].geometry.location;
        setPostcodeLongLat([location.lng, location.lat]);
        setMapCenter([location.lng, location.lat]);
      })
      .catch(err => console.log(err));
  };

  const goToAddress = address => {
    // Initialise areas?
    let baseNearestHUBs = nearestHUBs;

    if (!nearestHUBs) {
      baseNearestHUBs = [];

      const areas = [
        'Brent',
        'Camden',
        'Camden Phase 2',
        'Barnet',
        'Barnet Phase 2',
        'Harrow',
        'Bexley'
      ];

      areas.forEach(a => {
        baseNearestHUBs.push({
          localAuthority: a,
          hub: null,
          distance: 99999999
        });
      });
    }

    const newNearestHUBs = baseNearestHUBs.map(h => ({
      localAuthority: h.localAuthority,
      hub: null,
      distance: 99999999
    }));
    setNearestHUBs(newNearestHUBs);

    if (address) setCenterByPostcode(address);
  };

  const hydraDescription = hydraLocation ? hydraLocation.description : '';

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column width={12}>
            <Header as="h2">{`HUB Status: ${hydraDescription}`}</Header>
            <Dropdown
              style={{ width: '300px', backgroundColor: 'white' }}
              button
              text={`Location: ${hydraDescription}`}
            >
              <Dropdown.Menu scrolling>
                {areas.map(area => (
                  <>
                    <Dropdown.Header content={area} />
                    {hubLocationInfo
                      .filter(hl => hl.local_authority === area)
                      .map(hl => (
                        <Dropdown.Item
                          onClick={() => {
                            setHydraLocation(hl);
                            setFindAddress('');
                            goToAddress('');
                          }}
                        >
                          {hl.description}
                        </Dropdown.Item>
                      ))}
                  </>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>
            <Input
              placeholder="Find address/postcode"
              type="text"
              name="findAddress"
              value={findAddress}
              onChange={(_, { value }) => {
                setFindAddress(value);
              }}
            />
          </Grid.Column>
          <Grid.Column width={2}>
            <Button
              onClick={() => {
                goToAddress(findAddress);
              }}
              color="olive"
            >
              Find address
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row />
        {nearestHUBs ? (
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as="h3">
                <Icon name="table" />
                Nearest HUBs
              </Header>
              <Table basic="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Local Authority</Table.HeaderCell>
                    <Table.HeaderCell>HUB</Table.HeaderCell>
                    <Table.HeaderCell>Distance</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {nearestHUBs
                    ? nearestHUBs
                        .filter(h => h.hub !== null)
                        .map((h, h_idx) => (
                          <Table.Row key={h_idx}>
                            <Table.Cell>{h.localAuthority}</Table.Cell>
                            <Table.Cell>{h.hub.description}</Table.Cell>
                            <Table.Cell>
                              {Math.round(h.distance * 100) / 100} miles
                            </Table.Cell>
                          </Table.Row>
                        ))
                    : ''}
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        ) : (
          ''
        )}
        <Grid.Row>
          <Grid.Column>
            <Map
              center={mapCenter}
              zoom={mapZoom}
              setZoom={setMapZoom}
              dropPinLongLat={postcodeLongLat}
              showHydras={showHUBs}
              showAons={false}
              showUnderConstruction={showUnderConstruction}
              showCustomerView={showCustomerView}
              showControlledParkingZones={showControlledParkingZones}
              setPhalanx={setPhalanx}
              showCircle={false}
              filterHydraRef={null}
              circleCenter={postcodeLongLat}
              exportParkingData={exportParkingData}
              nightMode={nightMode}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <br />
      <br />
      <Checkbox
        toggle
        label={'Show DEICER view'}
        checked={showCustomerView}
        onChange={e => setShowCustomerView(!showCustomerView)}
      />
      <br />
      <br />
      <Checkbox
        toggle
        label={'Show HUB charge points'}
        checked={showHUBs}
        onChange={e => setShowHUBs(!showHUBs)}
        disabled={showCustomerView}
      />
      <br />
      <br />
      {/*<Checkbox
        toggle
        label={'Show HOME charge points'}
        checked={showAONs}
        onChange={e => setShowAONs(!showAONs)}
        disabled={showCustomerView}
      />*/}
      <Checkbox
        toggle
        label={'Show controlled parking zones'}
        checked={showControlledParkingZones}
        onChange={e =>
          setShowControlledParkingZones(!showControlledParkingZones)
        }
      />
      <br />
      <br />
      <Checkbox
        toggle
        label={'Show streets under construction'}
        checked={showUnderConstruction}
        onChange={e => setShowUnderConstruction(!showUnderConstruction)}
        disabled={showCustomerView}
      />
      <br />
      <br />
      {/*<Checkbox
        toggle
        label={'Show 10-minute walking radius'}
        checked={showCircle}
        onChange={e => setShowCircle(!showCircle)}
        disabled={showCustomerView}
      />*/}
      <Checkbox
        toggle
        label={'Night mode'}
        checked={nightMode}
        onChange={e => setNightMode(!nightMode)}
      />
      <br />
      <br />
      <Button
        onClick={() => {
          if (!exportParkingData) setExportParkingData(true);
          else setExportParkingData(false);
        }}
        color="olive"
      >
        Export Parking Data
      </Button>
    </Segment>
  );
};
